<script>
// Ajustar esse arquivo para padrão Responza.
import { useVuelidate } from '@vuelidate/core';
import { required, minValue, maxValue } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { parseBoolean } from '@chatwoot/utils';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import semver from 'semver';
import { getLanguageDirection } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import FormSection from 'dashboard/components/FormSection.vue';
import FormButton from 'v3/components/Form/Button.vue';
import FormInput from 'v3/components/Form/Input.vue';
import FormSelect from 'v3/components/Form/Select.vue';
import PlanSelection from 'v3/views/auth/signup/components/PlanSelection.vue';
import ModularPlanSetup from 'v3/views/auth/signup/components/ModularPlanSetup.vue';
import SubscriptionAPI from '../../../../api/subscription';

const PLAN_IDS = ['basic', 'pro', 'max'];
const MODULAR_MODULE_KEYS = [
  'auto_broadcasts',
  'scheduling',
  'products_services',
  'api_integration',
];
const MODULAR_QUANTITY_KEYS = [
  'additional_attendants',
  'additional_channels',
  'additional_ai_agents',
  'additional_storage',
  'extra_ai_tokens',
];

const EMPTY_PLAN_PRICES = PLAN_IDS.reduce((acc, planId) => {
  acc[planId] = null;
  return acc;
}, {});

const EMPTY_MODULAR_PRICING = {
  base: null,
  modules: MODULAR_MODULE_KEYS.reduce((acc, key) => {
    acc[key] = null;
    return acc;
  }, {}),
  quantities: MODULAR_QUANTITY_KEYS.reduce((acc, key) => {
    acc[key] = null;
    return acc;
  }, {}),
};

const buildDefaultModularPlanConfig = (basePrice = null) => ({
  base_price: basePrice,
  modules: MODULAR_MODULE_KEYS.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {}),
  quantities: MODULAR_QUANTITY_KEYS.reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {}),
});

const cloneModularPricing = pricing => ({
  base: pricing.base,
  modules: { ...pricing.modules },
  quantities: { ...pricing.quantities },
});

export default {
  components: {
    FormSection,
    FormButton,
    FormInput,
    FormSelect,
    PlanSelection,
    ModularPlanSetup,
  },
  setup() {
    const { updateUISettings } = useUISettings();
    const { enabledLanguages } = useConfig();
    const { accountId } = useAccount();
    const v$ = useVuelidate();

    return { updateUISettings, v$, enabledLanguages, accountId };
  },
  data() {
    return {
      id: '',
      name: '',
      locale: 'en',
      domain: '',
      supportEmail: '',
      cnpj: '',
      phone: '',
      features: {},
      autoResolveDuration: null,
      latestChatwootVersion: null,
      // Subscription data
      subscriptionStatus: null,
      subscriptionEndsOn: null,
      trialEndsAt: null,
      planId: null,
      stripeSubscriptionId: null,
      cancelAtPeriodEnd: false,
      isLoadingCheckout: false,
      isLoadingCancel: false,
      isLoadingPlanPrices: false,
      showPlanModal: false,
      planModalStep: 'plans',
      selectedPlanForCheckout: null,
      showManageDropdown: false,
      planPrices: { ...EMPTY_PLAN_PRICES },
      planConfiguration: null,
      modularPricing: cloneModularPricing(EMPTY_MODULAR_PRICING),
      modularPlanDraft: buildDefaultModularPlanConfig(),
      inputStyles: {
        borderRadius: '12px',
        padding: '6px 12px',
        fontSize: '14px',
        marginBottom: '2px',
      },
      selectStyles: {
        borderRadius: '12px',
        fontSize: '14px',
        marginBottom: '2px',
      },
    };
  },
  validations: {
    name: {
      required,
    },
    locale: {
      required,
    },
    autoResolveDuration: {
      minValue: minValue(1),
      maxValue: maxValue(999),
    },
  },
  computed: {
    ...mapGetters({
      globalConfig: 'globalConfig/get',
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    showAutoResolutionConfig() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.AUTO_RESOLVE_CONVERSATIONS
      );
    },
    hasAnUpdateAvailable() {
      if (!semver.valid(this.latestChatwootVersion)) {
        return false;
      }

      return semver.lt(
        this.globalConfig.appVersion,
        this.latestChatwootVersion
      );
    },
    languagesSortedByCode() {
      const enabledLanguages = [...this.enabledLanguages];
      return enabledLanguages.sort((l1, l2) =>
        l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
      );
    },
    languageOptions() {
      return this.languagesSortedByCode.map(lang => ({
        value: lang.iso_639_1_code,
        label: lang.name,
      }));
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },

    featureInboundEmailEnabled() {
      return !!this.features?.inbound_emails;
    },

    featureCustomReplyDomainEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_domain
      );
    },

    featureCustomReplyEmailEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_email
      );
    },

    getAccountId() {
      return this.id.toString();
    },
    accountInitials() {
      if (!this.name) return '';
      const words = this.name.trim().split(' ');
      if (words.length === 1) {
        return words[0].charAt(0).toUpperCase();
      }
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    },
    // Subscription computed properties
    hasActiveSubscription() {
      return this.subscriptionStatus === 'active';
    },
    isTrialingSubscription() {
      return this.subscriptionStatus === 'trialing';
    },
    hasStripeSubscription() {
      return !!this.stripeSubscriptionId;
    },
    isIncompleteSubscription() {
      return this.subscriptionStatus === 'incomplete';
    },
    isPaymentFailed() {
      return this.subscriptionStatus === 'payment_failed' || this.subscriptionStatus === 'past_due';
    },
    hasSelectedPlanWithoutSubscription() {
      return (
        this.planId &&
        (!this.subscriptionStatus || this.subscriptionStatus === 'pending') &&
        !this.trialEndsAt
      );
    },
    isTrialExpired() {
      if (!this.trialEndsAt) return false;
      return new Date() > new Date(this.trialEndsAt);
    },
    daysUntilExpiry() {
      const endDate = this.hasActiveSubscription
        ? this.subscriptionEndsOn
        : this.trialEndsAt;
      if (!endDate) return null;
      const diff = new Date(endDate) - new Date();
      return Math.ceil(diff / (1000 * 60 * 60 * 24));
    },
    subscriptionStatusText() {
      // Prioridade: incomplete > payment_failed > active > trial
      if (this.isIncompleteSubscription) {
        return this.$t('SUBSCRIPTION_SECTION.STATUSES.PENDING_PAYMENT');
      }
      if (this.isPaymentFailed) {
        return this.$t('SUBSCRIPTION_SECTION.STATUSES.PAYMENT_FAILED');
      }
      if (this.hasActiveSubscription) {
        if (this.cancelAtPeriodEnd) {
          return this.$t('SUBSCRIPTION_SECTION.STATUSES.CANCELED');
        }
        return this.$t('SUBSCRIPTION_SECTION.STATUSES.ACTIVE');
      }
      if (this.subscriptionStatus === 'canceled') {
        return this.$t('SUBSCRIPTION_SECTION.STATUSES.CANCELED');
      }
      if (this.hasSelectedPlanWithoutSubscription) {
        if (this.isModularPlan) {
          return this.$t('SUBSCRIPTION_SECTION.STATUSES.MODULAR_CONFIGURED');
        }
        return this.$t('SUBSCRIPTION_SECTION.STATUSES.AWAITING_SUBSCRIPTION');
      }
      if (this.trialEndsAt) {
        if (this.isTrialExpired) {
          return this.$t('SUBSCRIPTION_SECTION.STATUSES.TRIAL_EXPIRED');
        }
        return this.$t('SUBSCRIPTION_SECTION.STATUSES.TRIAL_REMAINING', { days: this.daysUntilExpiry });
      }
      return this.$t('SUBSCRIPTION_SECTION.STATUSES.NO_SUBSCRIPTION');
    },
    subscriptionStatusClass() {
      if (this.isIncompleteSubscription) {
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      }
      if (this.isPaymentFailed) {
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      }
      if (this.hasActiveSubscription) {
        if (this.cancelAtPeriodEnd) {
          return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        }
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      }
      if (this.subscriptionStatus === 'canceled') {
        return 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200';
      }
      if (this.hasSelectedPlanWithoutSubscription) {
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      }
      if (this.isTrialExpired) {
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      }
      if (this.daysUntilExpiry !== null && this.daysUntilExpiry <= 7) {
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      }
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    },
    planName() {
      const planKey = this.planId?.toUpperCase() || 'PRO';
      return this.$t(`PLANS.${planKey}.NAME`);
    },
    planPriceLabel() {
      return this.isModularPlan
        ? this.$t('SUBSCRIPTION_SECTION.ESTIMATED_VALUE')
        : this.$t('SUBSCRIPTION_SECTION.VALUE');
    },
    planPrice() {
      if (this.isModularPlan) {
        const total = this.calculateModularTotal(this.planConfiguration);
        return total !== null
          ? this.formatPrice(total)
          : this.$t('PLANS.PRICE_UNAVAILABLE');
      }
      return this.isPlanPriceAvailable(this.planId)
        ? this.formatPrice(this.planPrices[this.planId])
        : this.$t('PLANS.PRICE_UNAVAILABLE');
    },
    isCurrentPlanPriceAvailable() {
      if (this.isModularPlan) {
        return this.calculateModularTotal(this.planConfiguration) !== null;
      }
      return this.isPlanPriceAvailable(this.planId);
    },
    formattedExpiryDate() {
      const endDate = this.hasActiveSubscription
        ? this.subscriptionEndsOn
        : this.trialEndsAt;
      if (!endDate) return null;
      return new Date(endDate).toLocaleDateString('pt-BR');
    },
    hasExpiryDate() {
      return !!(
        (this.hasActiveSubscription && this.subscriptionEndsOn) ||
        (!this.hasActiveSubscription && this.trialEndsAt)
      );
    },
    showSubscribeButton() {
      return !this.hasActiveSubscription && !this.isTrialingSubscription;
    },
    hasSubscriptionData() {
      return this.trialEndsAt || this.subscriptionStatus || this.planId;
    },
    showSubscriptionSection() {
      // Só mostra a seção de assinatura se ENABLE_ACCOUNT_SIGNUP estiver ativado
      return parseBoolean(window.chatwootConfig.signupEnabled);
    },
    isModularPlan() {
      return this.planId === 'modular';
    },
    planSelectionPricing() {
      return {
        fixed: {
          basic: { amount: this.planPrices.basic },
          pro: { amount: this.planPrices.pro },
          max: { amount: this.planPrices.max },
        },
        modular: {
          base: { amount: this.modularPricing.base },
        },
      };
    },
  },
  mounted() {
    this.initializeAccount();
    // Só verifica status da assinatura se a funcionalidade estiver habilitada
    if (this.showSubscriptionSection) {
      this.checkSubscriptionStatus();
      this.refreshPlanPrices();
    }
    // Adiciona listener para fechar dropdown ao clicar fora
    document.addEventListener('click', this.closeDropdownOnOutsideClick);
  },
  beforeUnmount() {
    // Remove listener ao desmontar o componente
    document.removeEventListener('click', this.closeDropdownOnOutsideClick);
  },
  methods: {
    formatCNPJ(cnpj) {
      if (!cnpj || cnpj.length !== 14) return cnpj || 'Não informado';
      return `${cnpj.slice(0,2)}.${cnpj.slice(2,5)}.${cnpj.slice(5,8)}/${cnpj.slice(8,12)}-${cnpj.slice(12,14)}`;
    },
    formatPhone(phone) {
      const formatted = this.formatPhoneValue(phone);
      return formatted || 'Não informado';
    },
    onPhoneFocus() {
      const prefix = '+55 ';
      // Adiciona o prefixo quando o campo recebe foco
      if (!this.phone || this.phone === '') {
        this.phone = prefix;
      } else if (!this.phone.startsWith(prefix)) {
        this.phone = prefix + this.phone;
      }
    },
    onPhoneBlur() {
      const prefix = '+55 ';
      // Remove o prefixo se o usuário não digitou nada
      if (this.phone === prefix) {
        this.phone = '';
      }
    },
    formatPhoneForEdit(event) {
      const prefix = '+55 ';
      let value = event.target.value;

      // Garante que sempre começa com o prefixo
      if (!value.startsWith(prefix)) {
        value = prefix + value.replace(/^\+55\s*/, '');
      }

      // Impede de deletar o prefixo
      if (value.length < prefix.length) {
        this.phone = prefix;
        return;
      }

      // Remove tudo exceto dígitos após o prefixo
      let numbers = value.substring(prefix.length).replace(/[^\d]/g, '');

      if (numbers.length > 11) {
        numbers = numbers.substr(0, 11);
      }

      // Formato: +55 (XX) XXXXX-XXXX ou +55 (XX) XXXX-XXXX
      let formatted = prefix;
      if (numbers.length > 0) {
        if (numbers.length <= 2) {
          formatted += `(${numbers}`;
        } else if (numbers.length <= 7) {
          formatted += `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
        } else if (numbers.length <= 10) {
          formatted += `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
        } else {
          // Celular com 9 dígitos
          formatted += `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
        }
      }

      this.phone = formatted;
    },
    formatPhoneValue(value = '') {
      let digits = `${value}`.replace(/[^\d]/g, '');
      if (!digits) return '';
      digits = digits.slice(0, 11);
      if (digits.length <= 2) {
        return `(${digits}`;
      }
      if (digits.length <= 7) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
      }
      if (digits.length <= 10) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
      }
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    },
    calculateModularTotal(config) {
      const base = Number(this.modularPricing.base);
      if (!Number.isFinite(base)) return null;

      const modules = config?.modules || {};
      const quantities = config?.quantities || {};
      let total = base;

      for (const key of Object.keys(this.modularPricing.modules)) {
        const enabled =
          modules[key] === true ||
          modules[key] === 'true' ||
          modules[key] === 1 ||
          modules[key] === '1';
        if (enabled) {
          const price = this.modularPricing.modules[key];
          if (!Number.isFinite(price)) return null;
          total += price;
        }
      }

      for (const key of Object.keys(this.modularPricing.quantities)) {
        const qty = Number(quantities[key]) || 0;
        if (!qty) continue;
        const price = this.modularPricing.quantities[key];
        if (!Number.isFinite(price)) return null;
        total += qty * price;
      }

      return total;
    },
    async refreshPlanPrices() {
      if (this.isLoadingPlanPrices) return;
      this.isLoadingPlanPrices = true;
      try {
        const response = await SubscriptionAPI.fetchPrices();
        this.applyPricingPayload(response.data);
      } catch (error) {
      } finally {
        this.isLoadingPlanPrices = false;
      }
    },
    applyPricingPayload(data) {
      if (!data) return;
      const parseAmount = value => {
        if (value === null || value === undefined) return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };

      const fixed = data.fixed || {};
      const updatedPlanPrices = { ...this.planPrices };

      ['basic', 'pro', 'max'].forEach(planId => {
        const amount = parseAmount(fixed?.[planId]?.amount);
        updatedPlanPrices[planId] = amount;
      });

      this.planPrices = updatedPlanPrices;

      const modular = data.modular || {};
      const updatedModularPricing = cloneModularPricing(this.modularPricing);

      const baseAmount = parseAmount(modular?.base?.amount);
      updatedModularPricing.base = baseAmount;

      Object.keys(updatedModularPricing.modules).forEach(key => {
        const amount = parseAmount(modular?.modules?.[key]?.amount);
        updatedModularPricing.modules[key] = amount;
      });

      Object.keys(updatedModularPricing.quantities).forEach(key => {
        const amount = parseAmount(modular?.quantities?.[key]?.amount);
        updatedModularPricing.quantities[key] = amount;
      });

      this.modularPricing = updatedModularPricing;
    },
    shouldUpdateSubscription() {
      return this.hasStripeSubscription;
    },
    applySubscriptionUpdate(data) {
      if (!data) return;
      if (data.plan_id !== undefined) {
        this.planId = data.plan_id;
      }
      if (data.subscription_status !== undefined) {
        this.subscriptionStatus = data.subscription_status;
      }
      if (data.subscription_ends_on !== undefined) {
        this.subscriptionEndsOn = data.subscription_ends_on;
      }
      if (data.trial_ends_at !== undefined) {
        this.trialEndsAt = data.trial_ends_at;
      }
      if (data.cancel_at_period_end !== undefined) {
        this.cancelAtPeriodEnd = data.cancel_at_period_end;
      }
      if (data.stripe_subscription_id !== undefined) {
        this.stripeSubscriptionId = data.stripe_subscription_id;
      }
      if (Object.prototype.hasOwnProperty.call(data, 'plan_configuration')) {
        this.planConfiguration = data.plan_configuration;
      }
    },
    async initializeAccount() {
      try {
        const {
          name,
          locale,
          id,
          domain,
          support_email,
          custom_attributes,
          features,
          auto_resolve_duration,
          latest_chatwoot_version: latestChatwootVersion,
        } = this.getAccount(this.accountId);

        this.$root.$i18n.locale = locale;
        this.name = name;
        this.locale = locale;
        this.id = id;
        this.domain = domain;
        this.supportEmail = support_email;
        this.cnpj = custom_attributes?.cnpj || '';
        // Inicializa o telefone sem o prefixo (será adicionado ao focar)
        let phoneFromDB = custom_attributes?.phone || '';
        // Remove o código 55 se vier do banco (para evitar duplicação)
        const phoneDigits = phoneFromDB.replace(/[^\d]/g, '');
        if (phoneDigits.startsWith('55') && phoneDigits.length >= 12) {
          phoneFromDB = phoneDigits.substring(2); // Remove os primeiros 2 dígitos (55)
        }
        this.phone = this.formatPhoneValue(phoneFromDB);
        this.features = features;
        this.autoResolveDuration = auto_resolve_duration;
        this.latestChatwootVersion = latestChatwootVersion;
        // Subscription data
        this.subscriptionStatus = custom_attributes?.subscription_status || null;
        this.subscriptionEndsOn = custom_attributes?.subscription_ends_on || null;
        this.trialEndsAt = custom_attributes?.trial_ends_at || null;
        this.planId = custom_attributes?.plan_id || null;
        this.stripeSubscriptionId = custom_attributes?.stripe_subscription_id || null;
        this.cancelAtPeriodEnd = custom_attributes?.cancel_at_period_end || false;
        this.planConfiguration = custom_attributes?.plan_configuration || null;
      } catch (error) {
        // Ignore error
      }
    },

    async updateAccount() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'));
        return;
      }
      try {
        // Remove o prefixo +55 e qualquer formatação antes de salvar
        const phoneDigits = this.phone.replace(/[^\d]/g, '');

        await this.$store.dispatch('accounts/update', {
          locale: this.locale,
          name: this.name,
          domain: this.domain,
          support_email: this.supportEmail,
          auto_resolve_duration: this.autoResolveDuration,
          custom_attributes: {
            cnpj: this.cnpj,
            phone: phoneDigits,
            phone_country: 'br',
            phone_dial_code: '+55',
          },
        });
        this.$root.$i18n.locale = this.locale;
        this.getAccount(this.id).locale = this.locale;
        this.updateDirectionView(this.locale);
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },

    updateDirectionView(locale) {
      const isRTLSupported = getLanguageDirection(locale);
      this.updateUISettings({
        rtl_view: isRTLSupported,
      });
    },
    async onCopyAccountId() {
      await copyTextToClipboard(this.getAccountId);
      useAlert(this.$t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
    },
    openPlanModal() {
      this.showPlanModal = true;
      this.refreshPlanPrices();
      if (this.isModularPlan) {
        this.openModularPlanSetup();
      } else {
        this.planModalStep = 'plans';
      }
    },
    async cancelSubscription() {
      const confirmMessage = this.$t(
        'SUBSCRIPTION_SECTION.ALERTS.CANCEL_CONFIRM'
      );
      if (!window.confirm(confirmMessage)) return;

      this.isLoadingCancel = true;
      try {
        const response = await SubscriptionAPI.cancelSubscription();
        this.applySubscriptionUpdate(response.data);
        useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.CANCEL_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.CANCEL_ERROR'));
      } finally {
        this.isLoadingCancel = false;
      }
    },
    closePlanModal() {
      this.showPlanModal = false;
      this.selectedPlanForCheckout = null;
      this.planModalStep = 'plans';
      this.modularPlanDraft = buildDefaultModularPlanConfig(
        this.modularPricing.base
      );
    },
    formatPrice(price) {
      if (!Number.isFinite(price)) {
        return this.$t('PLANS.PRICE_UNAVAILABLE');
      }
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(price);
    },
    isPlanPriceAvailable(planId) {
      if (!planId) return false;
      if (planId === 'modular') {
        return Number.isFinite(this.modularPricing.base);
      }
      return Number.isFinite(this.planPrices[planId]);
    },
    isModulePriceAvailable(moduleId) {
      return Number.isFinite(this.modularPricing.modules[moduleId]);
    },
    isQuantityPriceAvailable(itemId) {
      return Number.isFinite(this.modularPricing.quantities[itemId]);
    },
    isModularCheckoutAvailable(config) {
      const baseAvailable = Number.isFinite(this.modularPricing.base);
      if (!baseAvailable) return false;

      const modules = config?.modules || {};
      const quantities = config?.quantities || {};

      const missingModulePrice = Object.keys(modules).some(
        key => modules[key] && !this.isModulePriceAvailable(key)
      );
      if (missingModulePrice) return false;

      const missingQuantityPrice = Object.keys(quantities).some(
        key => Number(quantities[key]) > 0 && !this.isQuantityPriceAvailable(key)
      );
      if (missingQuantityPrice) return false;

      return true;
    },
    async selectPlanAndCheckout(plan) {
      if (this.isLoadingCheckout) return;
      if (!this.isPlanPriceAvailable(plan.id)) {
        useAlert(this.$t('PLANS.PRICE_UNAVAILABLE'));
        return;
      }
      if (plan.id === 'modular') {
        this.openModularPlanSetup();
        return;
      }
      if (this.shouldUpdateSubscription()) {
        await this.updateSubscription(plan.id);
        return;
      }
      this.selectedPlanForCheckout = plan.id;
      this.isLoadingCheckout = true;
      try {
        const response = await SubscriptionAPI.createCheckoutSession(plan.id);
        if (response.data?.checkout_url) {
          window.open(response.data.checkout_url, '_blank');
          this.closePlanModal();
        }
      } catch (error) {
        useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.CHECKOUT_ERROR'));
      } finally {
        this.isLoadingCheckout = false;
        this.selectedPlanForCheckout = null;
      }
    },
    openModularPlanSetup() {
      this.modularPlanDraft = this.isModularPlan && this.planConfiguration
        ? { ...this.planConfiguration }
        : buildDefaultModularPlanConfig(this.modularPricing.base);
      this.planModalStep = 'modular';
    },
    handleModularConfigUpdate(config) {
      this.modularPlanDraft = config;
    },
    handleModularBack() {
      this.planModalStep = 'plans';
    },
    async handleModularContinue(config) {
      if (this.isLoadingCheckout) return;
      if (!this.isModularCheckoutAvailable(config)) {
        useAlert(this.$t('PLANS.PRICE_UNAVAILABLE'));
        return;
      }
      if (this.shouldUpdateSubscription()) {
        await this.updateSubscription('modular', { plan_configuration: config });
        return;
      }
      this.isLoadingCheckout = true;
      try {
        await this.$store.dispatch('accounts/update', {
          custom_attributes: {
            plan_id: 'modular',
            plan_configuration: config,
          },
        });
        this.planId = 'modular';
        this.planConfiguration = config;
        useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.MODULAR_SAVED'));
      } catch (error) {
        useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.MODULAR_SAVE_ERROR'));
        return;
      }
      try {
        const response = await SubscriptionAPI.createCheckoutSession('modular', {
          plan_configuration: config,
        });
        if (response.data?.checkout_url) {
          window.open(response.data.checkout_url, '_blank');
          this.closePlanModal();
        }
      } catch (error) {
        useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.CHECKOUT_ERROR'));
      } finally {
        this.isLoadingCheckout = false;
      }
    },
    async updateSubscription(planId, extraPayload = {}) {
      this.selectedPlanForCheckout = planId;
      this.isLoadingCheckout = true;
      try {
        const response = await SubscriptionAPI.updateSubscription(planId, extraPayload);
        this.applySubscriptionUpdate(response.data);
        const requiresPayment = response.data?.requires_payment;
        if (requiresPayment) {
          if (response.data?.invoice_url) {
            window.open(response.data.invoice_url, '_blank');
          }
          useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.PAYMENT_REQUIRED'));
        } else {
          useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.SUBSCRIPTION_UPDATED'));
        }
        this.closePlanModal();
      } catch (error) {
        useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.SUBSCRIPTION_UPDATE_ERROR'));
      } finally {
        this.isLoadingCheckout = false;
        this.selectedPlanForCheckout = null;
      }
    },
    handleSubscribe() {
      // Só abre o modal se a funcionalidade estiver habilitada
      if (this.showSubscriptionSection) {
        // Sempre abre o modal para o usuário escolher o plano
        this.openPlanModal();
      }
    },
    toggleManageDropdown() {
      this.showManageDropdown = !this.showManageDropdown;
    },
    closeDropdownOnOutsideClick(event) {
      if (!event.target.closest('.manage-dropdown-container')) {
        this.showManageDropdown = false;
      }
    },
    async managePaymentMethods() {
      this.showManageDropdown = false;
      try {
        const response = await SubscriptionAPI.createPaymentMethodPortalSession();
        const portalUrl = response.data?.portal_url;
        if (portalUrl) {
          window.open(portalUrl, '_blank');
          return;
        }
        useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.PAYMENT_METHODS_ERROR'));
      } catch (error) {
        useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.PAYMENT_METHODS_ERROR'));
      }
    },
    checkSubscriptionStatus() {
      const { subscription, checkout_cancelled: checkoutCancelled } =
        this.$route.query;

      if (subscription === 'success') {
        useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.SUBSCRIPTION_ACTIVATED'));
        this.clearSubscriptionQuery();
      } else if (subscription === 'pending_payment') {
        useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.PENDING_PAYMENT'));
        this.clearSubscriptionQuery();
      } else if (checkoutCancelled) {
        useAlert(this.$t('SUBSCRIPTION_SECTION.ALERTS.CHECKOUT_CANCELLED'));
        this.clearSubscriptionQuery();
      }
    },
    clearSubscriptionQuery() {
      // Remove o query param da URL sem recarregar a página
      const query = { ...this.$route.query };
      delete query.subscription;
      delete query.checkout_cancelled;
      this.$router.replace({ query });
    },
  },
};
</script>

<template>
  <div class="grid py-16 px-5 font-inter mx-auto gap-1 sm:max-w-[720px]">
    <div class="flex flex-col gap-8">
      <h2 class="text-2xl font-medium text-ash-900">
        <woot-sidemenu-icon />
        {{ $t('GENERAL_SETTINGS.TITLE') }}
      </h2>
      <div>
        <p class="text-sm mb-2 text-n-slate-12 font-medium">{{ $t('PROFILE_SETTINGS.FORM.PICTURE') }}</p>
        <div class="w-[72px] h-[72px] bg-woot-75 dark:bg-woot-800 rounded-xl flex items-center justify-center">
          <span class="text-2xl font-medium text-woot-600 dark:text-woot-500 select-none">
            {{ accountInitials }}
          </span>
        </div>
      </div>
    </div>

    <form v-if="!uiFlags.isFetchingItem" class="flex flex-col gap-4" @submit.prevent="updateAccount">
      <div class="flex flex-col gap-6">
        <p class="mb-0 text-sm font-normal text-ash-900">
          {{ $t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE') }}
        </p>
        <div class="flex flex-col gap-4">
          <form-input
            v-model="name"
            name="account_name"
            :styles="inputStyles"
            :class="{ error: v$.name.$error }"
            :label="$t('GENERAL_SETTINGS.FORM.NAME.LABEL')"
            :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
            :error="v$.name.$error ? $t('GENERAL_SETTINGS.FORM.NAME.ERROR') : ''"
            @input="v$.name.$touch"
            @blur="v$.name.$touch"
          />

          <!-- Campo CNPJ (apenas leitura) -->
          <div class="form-group">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
              CNPJ
            </label>
            <div class="px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-700 dark:text-slate-200" style="border-radius: 12px;">
              {{ formatCNPJ(cnpj) }}
            </div>
          </div>

          <!-- Campo Telefone (editável para admin) -->
          <FormInput
            v-model="phone"
            name="phone"
            type="tel"
            :styles="inputStyles"
            label="Telefone"
            placeholder="(12) 34567-8910"
            @focus="onPhoneFocus"
            @blur="onPhoneBlur"
            @input="formatPhoneForEdit"
          />

          <label :class="{ error: v$.locale.$error }">
            {{ $t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL') }}
            <select v-model="locale" :style="selectStyles">
              <option v-for="lang in languagesSortedByCode" :key="lang.iso_639_1_code" :value="lang.iso_639_1_code">
                {{ lang.name }}
              </option>
            </select>
            <span v-if="v$.locale.$error" class="message">
              {{ $t('GENERAL_SETTINGS.FORM.LANGUAGE.ERROR') }}
            </span>
          </label>
          <form-input
            v-if="featureCustomReplyDomainEnabled"
            v-model="domain"
            name="domain"
            :styles="inputStyles"
            :label="$t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL')"
            :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
          />
          <form-input
            v-if="featureCustomReplyEmailEnabled"
            v-model="supportEmail"
            name="support_email"
            :styles="inputStyles"
            :label="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL')"
            :placeholder="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')"
          />
          <label v-if="showAutoResolutionConfig" :class="{ error: v$.autoResolveDuration.$error }">
            {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.LABEL') }}
            <input 
              v-model="autoResolveDuration" 
              type="number" 
              :style="inputStyles"
              :placeholder="$t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.PLACEHOLDER')"
              @blur="v$.autoResolveDuration.$touch" 
            />
            <span v-if="v$.autoResolveDuration.$error" class="message">
              {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.ERROR') }}
            </span>
          </label>
        </div>
      </div>

      <div class="mt-3 flex justify-start">
        <FormButton
          type="submit"
          color-scheme="primary"
          variant="solid"
          size="large"
          :loading="isUpdating"
        >
          Atualizar Configurações
        </FormButton>
      </div>

      <FormSection
        class="mt-10"
        :title="$t('GENERAL_SETTINGS.FORM.ACCOUNT_ID.TITLE')"
        :description="$t('GENERAL_SETTINGS.FORM.ACCOUNT_ID.NOTE')"
      >
        <div class="flex flex-row justify-between gap-4">
          <form-input
            name="account_id"
            label=""
            class="flex-1 [&>input]:!py-1.5 ltr:[&>input]:!pr-3 ltr:[&>input]:!pl-3 rtl:[&>input]:!pl-3 rtl:[&>input]:!pr-3 focus:[&>input]:!border-slate-200 focus:[&>input]:dark:!border-slate-600 [&>input]:cursor-not-allowed"
            :styles="{
              borderRadius: '12px',
              fontSize: '14px',
              marginBottom: '2px',
            }"
            :model-value="getAccountId"
            readonly
          />
          <FormButton
            type="button"
            size="large"
            icon="text-copy"
            variant="outline"
            color-scheme="secondary"
            @click="onCopyAccountId"
          >
            Copiar
          </FormButton>
        </div>
      </FormSection>
    </form>

    <woot-loading-state v-if="uiFlags.isFetchingItem" />
  </div>
</template>
